import { Component, input, output, computed } from '@angular/core';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-pagination',
  imports: [CommonModule],
  templateUrl: './pagination.html',
  styleUrl: './pagination.scss'
})
export class Pagination {
  // Inputs using signals
  totalItems = input<number>(0);
  pageSize = input<number>(10);
  currentPage = input<number>(1);
  pageSizeOptions = input<number[]>([5, 10, 25, 50, 100]);
  itemName = input<string>('entries');
  totalSelected = input<number>(0);

  // Outputs
  pageChange = output<number>();
  pageSizeChange = output<number>();
  clearSelection = output<void>();

  // Computed properties
  totalPages = computed(() => Math.ceil(this.totalItems() / this.pageSize()));

  showingFrom = computed(() => {
    if (this.totalItems() === 0) return 0;
    return (this.currentPage() - 1) * this.pageSize() + 1;
  });

  showingTo = computed(() => {
    const to = this.currentPage() * this.pageSize();
    return to > this.totalItems() ? this.totalItems() : to;
  });

  pages = computed(() => {
    const total = this.totalPages();
    const current = this.currentPage();
    const visiblePages: (number | string)[] = [];

    if (total <= 7) {
      for (let i = 1; i <= total; i++) visiblePages.push(i);
    } else {
      visiblePages.push(1);

      if (current > 4) {
        visiblePages.push('...');
      }

      const start = Math.max(2, current - 2);
      const end = Math.min(total - 1, current + 2);

      for (let i = start; i <= end; i++) {
        visiblePages.push(i);
      }

      if (current < total - 3) {
        visiblePages.push('...');
      }

      visiblePages.push(total);
    }

    return visiblePages;
  });

  // Methods
  onPageChange(page: number | string) {
    if (
      typeof page === 'number' &&
      page !== this.currentPage() &&
      page >= 1 &&
      page <= this.totalPages()
    ) {
      this.pageChange.emit(page);
    }
  }

  onPageSizeChange(event: Event) {
    const selectElement = event.target as HTMLSelectElement;
    const newSize = parseInt(selectElement.value, 10);
    this.pageSizeChange.emit(newSize);
  }

  isNumber(value: unknown): value is number {
    return typeof value === 'number';
  }
}
